import { useMemo } from "react";
import { useSnackbar, type SnackbarKey } from "notistack";

export interface ToastOptions {
  /** Override the 6s default auto-hide. */
  duration?: number;
  /** Keep the toast until dismissed. */
  persist?: boolean;
}

export interface Toast {
  success(message: string, options?: ToastOptions): SnackbarKey;
  error(message: string, options?: ToastOptions): SnackbarKey;
  warning(message: string, options?: ToastOptions): SnackbarKey;
  info(message: string, options?: ToastOptions): SnackbarKey;
  /** Dismiss one toast by key, or all when no key is given. */
  dismiss(key?: SnackbarKey): void;
}

/** The app's only notification API — replaces the legacy per-page snackbar trio. */
export function useToast(): Toast {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return useMemo(() => {
    const fire =
      (variant: "success" | "error" | "warning" | "info") =>
      (message: string, options?: ToastOptions): SnackbarKey =>
        enqueueSnackbar(message, {
          variant,
          autoHideDuration: options?.duration ?? 6000,
          persist: options?.persist,
        });

    return {
      success: fire("success"),
      error: fire("error"),
      warning: fire("warning"),
      info: fire("info"),
      dismiss: closeSnackbar,
    };
  }, [enqueueSnackbar, closeSnackbar]);
}
